import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import supabase from "../../supabaseClient";
import useAdminAuth from "../../utils/useAdminAuth";
import AuthGate from "../../Components/AuthGate";
import {
	Document,
	Page,
	Text,
	View,
	StyleSheet,
	PDFViewer,
	Image,
} from "@react-pdf/renderer";

import logo from "../../Images/GosellGardensLogo.png";

const styles = StyleSheet.create({
	page: {
		padding: 30,
		fontSize: 12,
		fontFamily: "Helvetica",
	},
	logo: {
		width: 100,
		height: 100,
		marginBottom: 20,
	},
	headerContainer: {
		marginBottom: 20,
	},
	billTo: {
		marginBottom: 10,
	},
	table: {
		display: "table",
		width: "auto",
		borderStyle: "solid",
		borderWidth: 1,
		borderColor: "#bfbfbf",
		marginBottom: 20,
	},
	tableRow: {
		flexDirection: "row",
	},
	tableCell: {
		flex: 1,
		borderStyle: "solid",
		borderWidth: 1,
		borderColor: "#bfbfbf",
		padding: 5,
	},
	totalCell: {
		flex: 2,
		borderStyle: "solid",
		borderWidth: 1,
		borderColor: "#bfbfbf",
		padding: 10,
	},
	total: {
		marginTop: 10,
		fontWeight: "bold",
	},
	paymentInstructions: {
		marginTop: 20,
		fontSize: 10,
	},
	paidStamp: {
		position: "absolute",
		top: "40%",
		left: 0,
		width: "100%",
		textAlign: "center",
		fontSize: 80,
		fontWeight: "bold",
		color: "#d32f2f",
		opacity: 0.5,
		transform: "rotate(-20deg)",
		zIndex: 1000,
		pointerEvents: "none",
	},
});

const BillTo = ({ invoice }) => (
	<View style={styles.billTo}>
		{invoice &&
			(invoice.customer_name ||
				invoice.customer_email ||
				invoice.phone_number) && (
				<Text style={{ marginBottom: 5, fontWeight: "bold" }}>Bill To:</Text>
			)}

		{invoice && invoice.customer_name && <Text>{invoice.customer_name}</Text>}
		{invoice && invoice.phone_number && <Text>{invoice.phone_number}</Text>}
		{invoice && invoice.customer_email && <Text>{invoice.customer_email}</Text>}
	</View>
);

const InvoiceItemsTable = ({ invoice }) => (
	<View style={styles.table}>
		<View style={styles.tableRow}>
			<Text style={styles.tableCell}>Description</Text>
			<Text style={styles.tableCell}>Quantity</Text>
			<Text style={styles.tableCell}>Price Per Item</Text>
		</View>
		{invoice.line_items.map((item, index) => (
			<View key={index} style={styles.tableRow}>
				<Text style={styles.tableCell}>{item.description}</Text>
				<Text style={styles.tableCell}>{item.quantity}</Text>
				<Text style={styles.tableCell}>${item.price.toFixed(2)}</Text>
			</View>
		))}
		<View style={styles.tableRow}>
			<Text
				style={[styles.totalCell, { textAlign: "right", fontWeight: "bold" }]}
			>
				Total:
			</Text>
			<Text style={[styles.tableCell, { fontWeight: "bold", paddingTop: 10 }]}>
				${invoice.total.toFixed(2)}
			</Text>
		</View>
	</View>
);

const InvoiceDocument = ({ invoice }) => (
	<Document title={`Invoice - ${invoice.customer_name}`}>
		<Page size="A4" style={styles.page} wrap>
			{invoice.status === "Paid" && <Text style={styles.paidStamp}>PAID</Text>}
			<Image style={styles.logo} src={logo} />
			<View style={styles.headerContainer}>
				<Text>Our Mailing Address:</Text>
				<Text>9226 Old Concord Blvd</Text>
				<Text>Inver Grove Heights, MN 55076</Text>
				<Text>Phone: (651) 239-4444</Text>
				<Text>Email: connor@gosellgardens.com</Text>
			</View>
			<Text
				style={{
					marginBottom: 5,
					fontWeight: "bold",
					textAlign: "right",
				}}
			>
				Invoice #: {invoice.invoice_number || "-"}
			</Text>
			<Text style={{ marginBottom: 5, textAlign: "right" }}>
				Invoiced on: {new Date(invoice.created_at).toLocaleDateString()}
			</Text>
			<Text style={{ marginBottom: 10, textAlign: "right" }}>
				Due Date:{" "}
				{invoice.due_date
					? new Date(invoice.due_date).toLocaleDateString()
					: "N/A"}
			</Text>
			<BillTo invoice={invoice} />
			<InvoiceItemsTable invoice={invoice} />
			<Text style={styles.paymentInstructions}>
				Please send payment to our mailing address or contact us for other
				payment arrangements. Thank you for your business!
			</Text>
		</Page>
	</Document>
);

const InvoicePage = () => {
	const { invoiceId } = useParams();
	const { authChecked, isLoggedIn, isAdmin } = useAdminAuth();
	const [invoice, setInvoice] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!authChecked || !isLoggedIn || !isAdmin) return;
		const fetchInvoice = async () => {
			setLoading(true);
			const { data, error } = await supabase
				.from("invoices")
				.select("*")
				.eq("id", invoiceId)
				.single();

			if (error) {
				console.error("Error fetching invoice:", error);
			} else {
				setInvoice(data);
			}
			setLoading(false);
		};
		console.log("Fetching invoice with ID:", invoiceId);
		fetchInvoice();
	}, [invoiceId, authChecked, isLoggedIn, isAdmin]);

	if (!authChecked) {
		return (
			<Box sx={{ mx: "auto", width: "100%", textAlign: "center", mt: 5 }}>
				<CircularProgress />
			</Box>
		);
	}
	if (!isLoggedIn) return <AuthGate variant="not-logged-in" />;
	if (!isAdmin) return <AuthGate variant="unauthorized" />;

	return (
		<Box sx={{ mx: "auto", width: "100%", textAlign: "center", mt: 5 }}>
			{loading ? (
				<CircularProgress />
			) : invoice ? (
				<>
					<Typography variant="h4" gutterBottom>
						Invoice #: {invoice.invoice_number || "-"}
					</Typography>
					<PDFViewer style={{ width: "100%", height: "80vh" }}>
						<InvoiceDocument invoice={invoice} />
					</PDFViewer>
				</>
			) : (
				<Typography variant="h6" color="error">
					Invoice not found.
				</Typography>
			)}
		</Box>
	);
};

export default InvoicePage;
